import { Head, usePage } from "@inertiajs/react"
import PublicLayout from "@/components/PublicLayout"
import { HeroBand, type HeroPhoto } from "@/components/gisela/HeroBand"
import { EnquiryFormSection } from "@/components/gisela/EnquiryForm"
import { sectionHidden } from "@/lib/section-visibility"
import type { PageMeta, PageProps } from "@/types/inertia"

interface Props {
  content: {
    hero: { heading?: string; subheading?: string; photo?: HeroPhoto | null; _hidden?: boolean }
    enquiry: { in_person_note?: string; _hidden?: boolean }
  }
  meta?: PageMeta
}

export default function GiselaContact({ content, meta }: Props) {
  const { props } = usePage<PageProps>()
  const tc = props.translations.gisela_pages.contact
  const phone = props.current_brand.phone
  const title = meta?.title ?? "Contact Gisela Ramirez | Start Therapy"
  const description =
    meta?.description ??
    "Start CBT or EMDR therapy with Gisela Ramirez — online worldwide, or in person in Prestwich and Hebden Bridge. Replies within one working day."

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <PublicLayout>
        {/* Hero */}
        {!sectionHidden(content.hero) && (
          <HeroBand photo={content.hero.photo} className="max-w-2xl">
            <h1 className="text-white">
              {content.hero.heading || props.translations.gisela_pages.contact.hero_heading}
            </h1>
            {content.hero.subheading && (
              <p className="mt-4 text-lg text-white/80">{content.hero.subheading}</p>
            )}
          </HeroBand>
        )}

        {/* Phone-led contact block — open editorial flow, the number is the
            headline act (valecoast-style: intro → branded heading → number → form) */}
        <section className="bg-cream pt-14 pb-4">
          <div className="mx-auto max-w-2xl px-6 lg:px-8">
            <p className="text-lg leading-relaxed text-ink-body">{tc.intro}</p>

            <div className="mt-12">
              <h2>
                {tc.contact_prefix} <span className="text-teal">{props.current_brand.name}</span>
              </h2>
              {phone && (
                <p className="mt-3">
                  <a
                    href={`tel:${phone.replace(/\s/g, "")}`}
                    className="text-4xl font-semibold tabular-nums text-teal no-underline sm:text-5xl"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    {phone}
                  </a>
                </p>
              )}
              <p className="mt-5 text-ink-muted">{tc.if_busy}</p>
            </div>
          </div>
        </section>

        {!sectionHidden(content.enquiry) && (
          <EnquiryFormSection inPersonNote={content.enquiry.in_person_note} />
        )}
      </PublicLayout>
    </>
  )
}
